import { renderUrlQrMarkup } from '../qr/render-qr';
import { openAppStore } from '../qr/deep-link';
import { renderAppleLogoSvg, renderGooglePlayLogoSvg } from './app-store-icons';

export type AppStorePlatform = 'ios' | 'android';

type PlatformCopy = {
  title: string;
  lead: string;
  qrHint: string;
  ctaLabel: string;
  dialogLabel: string;
  icon: string;
};

const PLATFORM_COPY: Record<AppStorePlatform, PlatformCopy> = {
  ios: {
    title: 'Download Zippy for iPhone',
    lead: 'Scan the code with your iPhone camera, or open the App Store directly on this device.',
    qrHint: 'Scan to open the App Store',
    ctaLabel: 'Open App Store',
    dialogLabel: 'Download Zippy on the App Store',
    icon: renderAppleLogoSvg('zippy-app-modal-platform-icon'),
  },
  android: {
    title: 'Download Zippy for Android',
    lead: 'Scan the code with your Android phone, or open Google Play directly on this device.',
    qrHint: 'Scan to open Google Play',
    ctaLabel: 'Open Google Play',
    dialogLabel: 'Get Zippy on Google Play',
    icon: renderGooglePlayLogoSvg('zippy-app-modal-platform-icon zippy-app-modal-platform-icon--play'),
  },
};

function isMobileLikeViewport(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(max-width: 520px), (pointer: coarse)').matches;
}

export class AppStoreDownloadModal {
  private overlay: HTMLElement | null = null;
  private escapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private focusTrapHandler: ((event: KeyboardEvent) => void) | null = null;
  private trigger: HTMLElement | null = null;

  constructor(private readonly shadow: ShadowRoot) {}

  open(platform: AppStorePlatform, url: string, trigger: HTMLElement): void {
    this.close();
    this.trigger = trigger;

    const copy = PLATFORM_COPY[platform];
    const mobile = isMobileLikeViewport();

    const overlay = document.createElement('div');
    overlay.className = `zippy-app-modal-overlay${mobile ? ' zippy-app-modal-overlay--mobile' : ''}`;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', copy.dialogLabel);

    overlay.innerHTML = `
      <div class="zippy-app-modal-panel">
        <button type="button" class="zippy-app-modal-close" aria-label="Close download dialog"><svg class="zippy-app-modal-close-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>
        <div class="zippy-app-modal-header">
          ${copy.icon}
          <div class="zippy-app-modal-heading">
            <h2 class="zippy-app-modal-title">${copy.title}</h2>
            <p class="zippy-app-modal-lead">${copy.lead}</p>
          </div>
        </div>
        <div class="zippy-app-modal-qr-wrap">
          <div class="zippy-app-modal-qr-frame" aria-label="${copy.qrHint}">${renderUrlQrMarkup(url, 5)}</div>
          <p class="zippy-app-modal-qr-hint">${copy.qrHint}</p>
        </div>
        <div class="zippy-app-modal-actions">
          <a class="zippy-app-modal-cta zippy-app-modal-cta--${platform}" href="${escapeAttribute(url)}" target="_blank" rel="noopener noreferrer">${copy.ctaLabel}</a>
          ${mobile ? `<p class="zippy-app-modal-mobile-note">On this device, use the button above to install the app.</p>` : ''}
        </div>
      </div>
    `;

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        this.close();
      }
    });

    overlay.querySelector('.zippy-app-modal-panel')?.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    overlay.querySelector('.zippy-app-modal-close')?.addEventListener('click', () => {
      this.close();
    });

    const cta = overlay.querySelector<HTMLAnchorElement>('.zippy-app-modal-cta');
    if (mobile && cta) {
      cta.addEventListener('click', (event) => {
        event.preventDefault();
        openAppStore(platform);
      });
    }

    this.shadow.appendChild(overlay);
    this.overlay = overlay;

    this.escapeHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);

    this.focusTrapHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !this.overlay) {
        return;
      }
      const focusables = Array.from(
        this.overlay.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (focusables.length === 0) {
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.focusTrapHandler);

    overlay.querySelector<HTMLElement>('.zippy-app-modal-close')?.focus();
  }

  close(): void {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.escapeHandler = null;
    }
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
    this.overlay?.remove();
    this.overlay = null;

    if (this.trigger?.isConnected) {
      this.trigger.focus();
    }
    this.trigger = null;
  }

  destroy(): void {
    this.close();
  }
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
